
import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Table, DatePicker, Row, Col, Space, Typography, message, Modal } from 'antd';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import { getPermission } from '../../constants';

const { Option } = Select;
const { Title } = Typography;

const userPermission = getPermission(); // Fetch permissions

const typeList = [
    ...(userPermission?.is_positive_adjustment ? [{ id: 'positive', name: 'Positive' }] : []),
    ...(userPermission?.is_negative_adjustment ? [{ id: 'negative', name: 'Negative' }] : [])
];


const PostAdjustment = () => {
    const [form] = Form.useForm();
    const [customers, setCustomers] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [products, setProducts] = useState([{ id: '', quantity: '', type: '', default: true, inventory_id: null }]);
    const [loading, setLoading] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [total, setTotal] = useState(0);
    const [selectedProductDetails, setSelectedProductDetails] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [currentProductIndex, setCurrentProductIndex] = useState(null);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await axiosInstance.get('/products');
                setProductsList(response.data.products);
            } catch (error) {
                message.error('Failed to fetch products.');
            }
        };
    //    console.log(userPermission);
        fetchProducts();
    }, []);

    const handleProductChange = (index, field, value) => {
        setProducts((prevProducts) => {
            const updatedProducts = [...prevProducts];

            if (field === 'id') {
                updatedProducts[index][field] = value;

                const selectedProduct = productsList.find((prod) => prod.id === value);

                if (selectedProduct) {
                    updatedProducts[index]['selling_price'] = selectedProduct.selling_price || 0;
                    updatedProducts[index]['buying_price'] = selectedProduct.buying_price || 0;
                }
            } else {
                updatedProducts[index][field] = value;
            }

            return updatedProducts;
        });
    };

    const addProductRow = () => {
        setProducts([...products, { id: '', quantity: '', type: '', default: true, inventory_id: null }]);
    };

    const removeProductRow = (index) => {
        const updatedProducts = [...products];
        updatedProducts.splice(index, 1);
        setProducts(updatedProducts);
    };

    const filterAccountsByType = (type) => {
        setFilteredAccounts(accounts.filter(account => account.type === type));
    };

    const confirmSubmit = (values) => {
        Modal.confirm({
            title: 'Confirm Sales Submission',
            content: 'Are you sure you want to submit this sales order?',
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => handleSubmit(values),
        });
    };

    const handleSubmit = async (values) => {
        setLoading(true);

        const payload = {
            products: products.map((product) => ({
                id: product.id,
                quantity: product.quantity,
                default: product.default,
                type: product.type,
                inventory_id: product.inventory_id || null
            })),
        };

        try {

            const createResponse = await axiosInstance.post('/adjustment/create', payload);

            if (createResponse.status === 200) {
                message.success('Product Adjusted Successfully!');
                form.resetFields();
                setProducts([{ id: '', quantity: '', type: '', default: true, inventory_id: null }]);
            } else {
                message.error('Failed to Adjust Product. Please try again.');
            }
        } catch (error) {
            if (error.response && error.response.data && error.response.data.error) {
                message.error(error.response.data.error);
            } else {
                message.error('An unexpected error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const openModal = async (index) => {
        try {
            const selectedProductId = products[index].id;
            const response = await axiosInstance.get(`/sales/sales-prices/${selectedProductId}`);
            if (response.data) {
                setSelectedProductDetails(response.data.details); // Adjust structure as needed
                setCurrentProductIndex(index); // Set the array index for mapping later
                setModalVisible(true); // Show the modal
            }
        } catch (error) {
            console.error('Failed to fetch product details:', error);
        }
    };


    const handleSelectPrice = (record) => {
        setProducts((prevProducts) => {
            return prevProducts.map((product, index) =>
                index === currentProductIndex
                    ? { ...product, default: record.default, inventory_id: record.default ? null : record.id }
                    : product
            );
        });
        setModalVisible(false); // Close the modal after price selection
    };

    const productColumns = [
        {
            title: 'Product',
            dataIndex: 'id',
            render: (_, record, index) => (
                <Select
                    showSearch
                    value={record.id}
                    onChange={(value) => handleProductChange(index, 'id', value)}
                    style={{ width: '200px' }}
                    placeholder="Select Product"
                    optionFilterProp="children" // Enables search based on the Option's text
                    filterOption={(input, option) =>
                        option?.children.toLowerCase().includes(input.toLowerCase())
                    } // Custom filter logic for better search
                >
                    {productsList.map((prod) => (
                        <Option key={prod.id} value={prod.id}>
                            {prod.name}
                        </Option>
                    ))}
                </Select>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            render: (_, record, index) => (
                <Space style={{ width: '100%' }}>
                    <Select
                        value={record.type}
                        onChange={(value) => handleProductChange(index, 'type', value)}
                        style={{ width: '200px' }} // Adjust width here
                        placeholder="Select Type"
                    >
                        <Option key="" value="">
                            Select
                        </Option>
                        {typeList.map((type) => (
                            <Option key={type.id} value={type.id}>
                                {type.name}
                            </Option>
                        ))}
                    </Select>
                    <Button onClick={() => openModal(index)}>Select Inventory</Button>
                </Space>
            ),
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.quantity}
                    onChange={(e) => handleProductChange(index, 'quantity', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    min={1}
                    placeholder="Enter Quantity"
                />
            ),
        },
        {
            title: 'Action',
            render: (_, __, index) => (
                index > 0 && (
                    <Button type="link" danger onClick={() => removeProductRow(index)}>
                        Remove
                    </Button>
                )
            ),
        },
    ];

    const calculateTotal = () => {
        let totalQuantity = 0;
        let totalPrice = 0;

        products.forEach(product => {
            totalQuantity += product.quantity;
            totalPrice += product.quantity * product.selling_price;
        });

        return { totalQuantity, totalPrice };
    };

    return (
        <div>
            <Title level={3}>Post Stock Adjustment</Title>
            <Form
                form={form}
                layout="vertical"
                onFinish={confirmSubmit}
            >

                <Table
                    rowKey="id"
                    columns={productColumns}
                    dataSource={products}
                    pagination={false}
                    size="small"
                    style={{ marginBottom: 20 }}
                    scroll={{ x: 'max-content' }}
                />
                <Button
                    type="dashed"
                    onClick={addProductRow}
                    block
                    style={{ marginBottom: 16, width: '200px' }}
                >
                    Add Product
                </Button>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        style={{ margin: '0 auto', width: '200px' }} // Reduced width and centered
                    >
                        Submit Sales Order
                    </Button>
                </Form.Item>
            </Form>
            <Modal
                title="Product Details"
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
            >
                <Table
                    dataSource={selectedProductDetails}
                    rowKey="id"
                    columns={[
                        { title: 'Buying Price', dataIndex: 'buying_price' },
                        { title: 'Selling Price', dataIndex: 'selling_price' },
                        { title: 'Quantity', dataIndex: 'quantity' },
                        { title: 'Expire Date', dataIndex: 'expire_date' },
                        {
                            title: 'Action',
                            render: (_, record) => (
                                <Button
                                    type="primary"
                                    onClick={() => handleSelectPrice(record)}
                                >
                                    Select
                                </Button>
                            ),
                        },
                    ]}
                    pagination={false}
                    footer={() => {
                        // Calculate the total quantity
                        const totalQuantity = selectedProductDetails.reduce((total, record) => total + record.quantity, 0);

                        return (
                            <div>
                                <strong>Total Quantity: {totalQuantity}</strong>
                            </div>
                        );
                    }}
                />
            </Modal>
        </div>
    );
};

export default PostAdjustment;


